import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Users, Calendar, UserCheck, X, ZoomIn } from "lucide-react";
import { fetchProject } from "../lib/api";

export default function ProgettoDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState(false);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    fetchProject(id).then(setItem).catch(() => setErr(true));
  }, [id]);

  if (err) {
    return (
      <div className="py-32 px-6 text-center" data-testid="project-detail-error">
        <h1 className="font-serif-display text-4xl text-[#A3133A] mb-4">Progetto non trovato</h1>
        <Link to="/progetti" className="text-[12px] tracking-eyebrow text-[#A3133A] link-underline">
          Torna ai progetti
        </Link>
      </div>
    );
  }

  if (!item) {
    return <div className="py-32 text-center text-[#5C5C5C]">Caricamento...</div>;
  }

  return (
    <article data-testid="page-project-detail">
      {/* Hero */}
      <section
        className="relative min-h-[60vh] flex items-end overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(28,8,16,0.55) 0%, rgba(163,19,58,0.85) 100%), url(${item.immagine_copertina})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-[1400px] w-full mx-auto px-6 md:px-12 lg:px-24 py-20 text-[#FBFBF9] relative z-10">
          <Link to="/progetti" data-testid="project-back" className="text-[11px] tracking-eyebrow opacity-90 inline-flex items-center gap-2 mb-6 link-underline">
            <ArrowLeft size={14} /> Tutti i progetti
          </Link>
          <div className="text-[11px] tracking-eyebrow opacity-85 mb-4 flex flex-wrap items-center gap-3">
            <span>{item.categoria}</span>
            <span className="opacity-40">·</span>
            <span>{item.periodo}</span>
            <span className="opacity-40">·</span>
            <span className="border border-white/40 px-3 py-0.5 rounded-sm">
            {item.status === "in-corso" ? "In corso" : item.status === "in-programma" ? "In programma" : "Concluso"}
            </span>
          </div>
          <h1 className="font-serif-display text-5xl sm:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            {item.titolo}
          </h1>
          {item.sottotitolo && (
            <p className="mt-6 text-lg max-w-2xl opacity-90 italic font-serif-display">
              {item.sottotitolo}
            </p>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="py-20 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">Il progetto</div>
          <h2 className="font-serif-display text-3xl text-[#1C1C1C] mb-6 leading-tight">{item.estratto}</h2>
          <div className="prose text-[#1C1C1C] text-base sm:text-lg leading-relaxed whitespace-pre-line">
            {item.descrizione}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="bg-[#A3133A] text-[#FBFBF9] p-7 relative grain overflow-hidden sticky top-28">
            <div className="relative z-10 space-y-5">
              <div>
                <div className="text-[10px] tracking-eyebrow opacity-80 mb-1">Periodo</div>
                <div className="flex items-center gap-2 text-base">
                  <Calendar size={14} className="opacity-90" />
                  {item.periodo}
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-eyebrow opacity-80 mb-1">Luogo</div>
                <div className="flex items-start gap-2 text-base">
                  <MapPin size={14} className="opacity-90 mt-1 shrink-0" />
                  <span>{item.luogo}</span>
                </div>
              </div>
              {item.partners && item.partners.length > 0 && (
                <div>
                  <div className="text-[10px] tracking-eyebrow opacity-80 mb-1">Partner</div>
                  <div className="flex items-start gap-2 text-base">
                    <Users size={14} className="opacity-90 mt-1 shrink-0" />
                    <ul className="space-y-1">
                      {item.partners.map((p, i) => (
                        <li key={p} className="text-sm">{p}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {item.coordinatori && item.coordinatori.length > 0 && (
                <div>
                  <div className="text-[10px] tracking-eyebrow opacity-80 mb-1">Coordinatori</div>
                  <div className="flex items-start gap-2 text-base">
                    <UserCheck size={14} className="opacity-90 mt-1 shrink-0" />
                    <ul className="space-y-1">
                      {item.coordinatori.map((c, i) => (
                        <li key={c} className="text-sm">{c}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>
      </section>

      {/* Gallery del progetto */}
      {item.immagini && item.immagini.length > 1 && (
        <section className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto pb-24">
          <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">Galleria del progetto</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {item.immagini.map((url, i) => (
              <button
                key={url}
                onClick={() => setZoom(url)}
                data-testid={`project-image-${i}`}
                className="group relative aspect-[4/3] overflow-hidden bg-[#A3133A]/5 border border-[#A3133A]/15 cursor-zoom-in"
              >
                <img src={url} alt={`${item.titolo} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <span className="absolute inset-0 flex items-center justify-center bg-[#A3133A]/0 group-hover:bg-[#A3133A]/30 transition">
                  <ZoomIn size={28} className="text-white opacity-0 group-hover:opacity-100 transition" />
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Lightbox */}
      {zoom && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 sm:p-10"
          onClick={() => setZoom(null)}
          data-testid="project-lightbox"
        >
          <button
            onClick={() => setZoom(null)}
            data-testid="project-lightbox-close"
            className="absolute top-5 right-5 text-white/80 hover:text-white transition p-2"
            aria-label="Chiudi"
          >
            <X size={30} />
          </button>
          <img
            src={zoom}
            alt={item.titolo}
            className="max-w-full max-h-[90vh] w-auto h-auto object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* CTA back */}
      <section className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto pb-24 text-center">
        <Link to="/progetti" className="text-[12px] tracking-eyebrow text-[#A3133A] inline-flex items-center gap-2 link-underline">
          <ArrowLeft size={14} /> Tutti i progetti della scuola
        </Link>
      </section>
    </article>
  );
}
